import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { fork } from "@formant/core/common/fork";
import { duration } from "@formant/core/model/duration";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IFilter } from "@formant/core/model/IFilter";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { IView } from "@formant/core/model/IView";
import { Timeout } from "@formant/core/model/Timeout";

import "@formant/react-grid-layout/css/styles.css";
import "@formant/react-resizable/css/styles.css";

import { track } from "../../analytics";
import {
    appStore,
    deviceDetailsStore,
    deviceStore,
    observeStore,
    onlineDevicesStore,
    operateStore,
    teleopSessionStore,
} from "../../stores";
import { ErrorPage } from "../ErrorPage";
import { ConfigurationPopup } from "../Operate/Configuration/ConfigurationPopup";
import { NoDevice } from "../Operate/Errors/NoDevice";
import { Navigation } from "../Operate/Navigation";
import { Teleop2Context } from "../Teleop/Teleop2Context";
import { TeleopDeviceHeader } from "../Teleop/TeleopDeviceHeader";
import { TeleopFeedback } from "../Teleop/TeleopFeedback";
import { TerminalView } from "../Teleop/TerminalView";
import { EditComment } from "./EditComment";
import { IssueCommandPopup } from "./IssueCommandPopup";
import { NewCommandPopup } from "./NewCommandPopup";
import { NoResults } from "./NoResults";
import * as styles from "./TeleopLayout.scss";

export interface ITeleopLayoutProps {
    view: IView;
    context: IFilter;
    device?: IDevice;
    id: string;
    layout: ITeleopLayout;
    onboarding: boolean;
    onChangeLayout: (layout: ITeleopLayout) => void;
}

@observer
export class TeleopLayout extends Component<ITeleopLayoutProps> {
    private unregister?: () => void;
    @observable
    private device?: false | IDevice;
    @observable
    private document?: IDeviceConfigurationDocument;
    private getConfigurationInterval?: Timeout;

    public async componentDidMount() {
        const { id } = this.props;

        await deviceStore.waitForConfiguration(id, true);
        this.device = deviceStore.get(id);
        operateStore.session = teleopSessionStore.get(id);
        await operateStore.session?.getDeviceConfiguration();
        this.getConfigurationInterval = setInterval(() => {
            this.document = operateStore.session?.deviceConfiguration?.document;
        }, 1 * duration.second);
    }

    public async componentWillUnmount() {
        const { session } = operateStore;
        const { getConfigurationInterval } = this;

        const durationMinutes = session?.startedTimestamp
            ? (new Date().getTime() - session.startedTimestamp) /
              duration.minute
            : undefined;

        if (durationMinutes) {
            track("Teleop Session Ended", {
                "Device ID": this.props.id,
                "Duration in Minutes": durationMinutes,
                "Teleop Session ID": session?.uuid,
                "Session ID": session?.sessionId,
            });
        }

        if (session) {
            teleopSessionStore.delete(session.deviceId);
            operateStore.session = undefined;
            await session.stop();
        }
        if (getConfigurationInterval) {
            clearInterval(getConfigurationInterval);
        }
        this.unregister?.();
    }

    public render() {
        const { id, onboarding, view } = this.props;
        const { preview } = appStore;
        const { session, feedbackVisible } = operateStore;
        const { editingComment } = observeStore;
        const { issuingCommand } = deviceStore;
        const { device, document } = this;
        const deviceDetails = deviceDetailsStore.get(id);

        if (device === undefined) {
            return false;
        }

        const onlineDevices = onlineDevicesStore.online();
        const isOffline =
            onlineDevices !== undefined &&
            device &&
            device?.id !== undefined &&
            !onlineDevices.has(device?.id);

        if (device === false || !device.enabled) {
            return <NoDevice device={device} />;
        }

        const context = {
            ...this.props.context,
            deviceIds: device.id ? [device.id] : undefined,
        };

        if (!session) {
            return (
                <ErrorPage
                    title="Session not available"
                    message="The session couldn't be created. The device might not be available."
                    reload={true}
                />
            );
        }

        if (!session.hasStarted) {
            fork(session.start());
            track("Teleop Session Started", {
                "Device ID": id,
                "Teleop Session ID": session?.uuid,
                "Session ID": session?.sessionId,
            });
        }

        return (
            <div className={styles.operate}>
                {isOffline ? (
                    <NoResults offline={true} />
                ) : (
                    <Teleop2Context
                        onChangeLayout={this.props.onChangeLayout}
                        context={context}
                        id={id}
                        device={device}
                        key={id}
                        view={view}
                        onboarding={onboarding}
                        session={session}
                        layout={this.props.layout}
                        onChangeViewId={() => {}}
                    />
                )}
                <>
                    <TerminalView session={session} onboarding={onboarding} />
                </>
                <>
                    <Navigation id={id} />
                </>

                {preview && device && (
                    <TeleopDeviceHeader device={device} offline={isOffline} />
                )}
                <TeleopFeedback
                    visible={feedbackVisible}
                    id={id}
                    sessionId={session?.sessionId}
                />
                {editingComment && (
                    <EditComment comment={editingComment} context={context} />
                )}
                {appStore.commandTemplate && deviceDetails && (
                    <NewCommandPopup
                        device={deviceDetails}
                        commandTemplate={appStore.commandTemplate}
                    />
                )}
                {issuingCommand && <IssueCommandPopup deviceId={id} />}

                {device && document && (
                    <ConfigurationPopup device={device} document={document} />
                )}
            </div>
        );
    }
}
